import { TeaseRowPeopleEntry } from "frontend/contentful/schema/blocks";
import { ProfilePageEntry } from "frontend/contentful/schema/pages";
import {
  PersonEntry,
  PersonWrapperEntry,
} from "frontend/contentful/schema/semantics";
import {
  TeaseRow,
  TeaseRowProps,
} from "design-system/components/blocks/tease-row/tease-row";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { createComponentHeaderProps } from "frontend/contentful/components/primitives/component-header.props";
import { createUseSearchProps } from "../../utils/use-search.props";
import { useSearch } from "frontend/hooks/use-search";
import {
  createArticleSearchPersonTease,
  createModernSearchPersonTease,
  createSearchPersonTease,
} from "frontend/contentful/search/search-person-tease";
import {
  FeaturedTermTopperFacultyHit,
  LegacySearchHit,
  ModernSearchHit,
} from "frontend/types";
import { useContentful } from "frontend/hooks/use-contentful";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { isSearchHitType } from "frontend/contentful/search/is-search-hit-type";
import { getHeadingLevel } from "../../utils/get-heading-level";
import { getPersonProps } from "../../utils/get-person.props";

interface TeaseRowPeopleOptions {
  headerAbove?: boolean;
  resetHeaders?: "h2" | "h3" | "h4";
}

export const ContentfulTeaseRowPeople = createContentfulComponent<
  TeaseRowPeopleEntry,
  TeaseRowPeopleOptions
>(({ entry, options }) => {
  const { getEntry, getEntryUrl } = useContentful();
  const headerAbove = options?.headerAbove ?? false;
  const resetHeaders = options?.resetHeaders ?? undefined;

  const headingLevel = getHeadingLevel(
    entry.fields.header !== undefined,
    false,
    headerAbove,
    resetHeaders,
  );
  const headingLevelTease = getHeadingLevel(
    entry.fields.header !== undefined,
    true,
    headerAbove,
    resetHeaders,
  );

  const props: TeaseRowProps = {
    className: "hbs-tease-row--person-tease",
    wrap: entry.fields.wrap ? "wrap" : "no-wrap",
    align: entry.fields.alignment!,
    headerContent: createComponentHeaderProps(entry.fields.header),
    teases: [],
    teasesPerRow: entry.fields.teasesPerRow,
    showPhone: entry.fields.showPhone,
    showJobTitle: entry.fields.showJobTitle,
    HeadingLevel: headingLevel,
    HeadingLevelTease: headingLevelTease,
  };

  entry.fields.teasesManual?.forEach((teaseEntryRef) => {
    const teaseEntry = getEntry(teaseEntryRef);
    if (!teaseEntry) return;

    let personEntry: PersonEntry | PersonWrapperEntry | undefined = undefined;
    let overrideLink: string | undefined = undefined;

    // Check if this is a ProfilePageEntry
    if (isContentType<ProfilePageEntry>("profilePage", teaseEntry)) {
      const profileRef = teaseEntry.fields.profile;
      if (profileRef) {
        const resolvedProfile = getEntry(profileRef);
        if (resolvedProfile) {
          personEntry = resolvedProfile;
          // Use the profile page URL as the link
          overrideLink = getEntryUrl(teaseEntry);
        }
      }
    } else {
      // It's a direct person entry
      personEntry = teaseEntry;
    }

    if (personEntry) {
      const personTeaseProps = getPersonProps(personEntry);

      if (personTeaseProps) {
        // Override the link if we extracted one from a profile page
        if (overrideLink) {
          personTeaseProps.link = overrideLink;
        }
        personTeaseProps.isTeaseRow = true;
        props.teases?.push({ name: "PersonTease", props: personTeaseProps });
      }
    }
  });

  if (entry.fields.teasesQuery) {
    const searchProps = createUseSearchProps(entry.fields.teasesQuery);
    const { data } = useSearch<
      LegacySearchHit | ModernSearchHit | FeaturedTermTopperFacultyHit
    >(searchProps);

    if (data?.hits) {
      data.hits.forEach((item) => {
        if (isSearchHitType<FeaturedTermTopperFacultyHit>("modern", item)) {
          const tease = createArticleSearchPersonTease(item);
          if (tease) {
            tease.forEach((tease) => {
              props.teases?.push({ name: "PersonTease", props: tease });
            });
          }
        } else if (isSearchHitType<LegacySearchHit>("legacy", item)) {
          const tease = createSearchPersonTease(item);
          if (tease) props.teases?.push({ name: "PersonTease", props: tease });
        } else if (isSearchHitType<ModernSearchHit>("modern", item)) {
          const tease = createModernSearchPersonTease(item);
          if (tease) props.teases?.push({ name: "PersonTease", props: tease });
        }
      });
    }
  }

  return <TeaseRow {...props} />;
});
